import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL_WITH_API } from '../config/api';
import { getExtensionId } from '../config/extension';
import { isStagingOrLocalhost } from '../utils/environment';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { clearAllAuthStorage, getEffectiveToken, getEffectiveUser, getImpersonationMeta, isImpersonating } from '../utils/authSession';
import HeaderUtilityMenu from '../components/HeaderUtilityMenu';
import FeedbackFab from '../components/FeedbackFab';
import { exportPolicyToPdf, PolicyPdfColorBlock } from '../utils/policyPdf';

interface PolicyColor {
  title: string;
  description: string;
}

interface LockStatus {
  lockedByAdmin: boolean;
  hasDomainAdmin: boolean;
  lockingAdminNames?: string[];
  message: string | null;
}

interface TeacherPolicyResponse {
  domain?: string;
  lockStatus?: LockStatus;
  colorScale?: 2 | 3 | 4 | 5;
  red: PolicyColor;
  orange?: PolicyColor;
  yellow: PolicyColor;
  green: PolicyColor;
  blue?: PolicyColor;
  fiveColorScaleEnabled?: boolean;
}

type AnyTemplateScale = 2 | 3 | 4 | 5;

const POLICY_META = {
  red: { label: 'Red policy', accent: '#ef4444', tint: 'rgba(239,68,68,0.04)' },
  orange: { label: 'Orange policy', accent: '#f59e0b', tint: 'rgba(245,158,11,0.08)' },
  yellow: { label: 'Yellow policy', accent: '#facc15', tint: 'rgba(254,249,195,0.72)' },
  green: { label: 'Green policy', accent: '#22c55e', tint: 'rgba(34,197,94,0.04)' },
  blue: { label: 'Blue policy', accent: '#2563eb', tint: 'rgba(37,99,235,0.14)' }
} as const;

const FOUR_COLOR_DEFAULTS: Required<Pick<TeacherPolicyResponse, 'red' | 'orange' | 'yellow' | 'green'>> = {
  red: {
    title: 'AI-Free (Prohibited)',
    description: 'Assignments or tasks must be completed entirely without AI tools. Users rely solely on their own knowledge, skills, and effort.'
  },
  orange: {
    title: 'AI-Assisted (Brainstorming/Structure)',
    description: 'AI is permitted for ideation, brainstorming, outlining, or structuring work, but no AI-generated content may appear in the final submission. Disclosure of tools used is required.'
  },
  yellow: {
    title: 'AI-Enhanced (Editing/Refining)',
    description: 'AI is permitted to improve the quality of human-created work, such as grammar checking, rephrasing, or summarizing. No new substantive content is created by AI. Disclosure is required.'
  },
  green: {
    title: 'AI-Empowered (Full Integration)',
    description: 'AI is used to generate core content, analyze data, or complete significant parts of the task. The user acts as editor and evaluator, ensuring accuracy and mitigating bias. Full transparency and citation of AI tools are required.'
  }
};

const FIVE_COLOR_DEFAULTS: Required<Pick<TeacherPolicyResponse, 'red' | 'orange' | 'yellow' | 'green' | 'blue'>> = {
  red: {
    title: 'No AI',
    description: 'The assessment is completed entirely without AI assistance in a controlled environment, ensuring that students rely solely on their existing knowledge, understanding, and skills.'
  },
  orange: {
    title: 'AI Planning',
    description: 'AI may be used for pre-task activities such as brainstorming, outlining, and initial research. This level focuses on the effective use of AI for planning, synthesis, and ideation, but assessments should emphasize the ability to develop and refine these ideas independently.'
  },
  yellow: {
    title: 'AI Collaboration',
    description: 'AI may be used to help complete the task, including idea generation, drafting, feedback, and refinement. Students should critically evaluate and modify the AI suggested outputs, demonstrating their understanding.'
  },
  green: {
    title: 'Full AI',
    description: 'AI may be used to complete any elements of the task, with students directing AI to achieve the assessment goals. Assessments at this level may also require engagement with AI to achieve goals and solve problems.'
  },
  blue: {
    title: 'AI Exploration',
    description: 'AI is used creatively to enhance problem-solving, generate novel insights, or develop innovative solutions to solve problems. Students and educators co-design assessments to explore unique AI applications within the field of study.'
  }
};

const THREE_COLOR_DEFAULTS: Pick<TeacherPolicyResponse, 'red' | 'yellow' | 'green'> = {
  red: FIVE_COLOR_DEFAULTS.red,
  yellow: FIVE_COLOR_DEFAULTS.yellow,
  green: FIVE_COLOR_DEFAULTS.green
};

const DEFAULT_POLICY: TeacherPolicyResponse = {
  colorScale: 3,
  red: THREE_COLOR_DEFAULTS.red,
  yellow: THREE_COLOR_DEFAULTS.yellow,
  green: THREE_COLOR_DEFAULTS.green
};

const normalizeScale = (rawScale?: number, fiveColorEnabled = false): AnyTemplateScale => {
  if (rawScale === 5 && fiveColorEnabled) return 5;
  if (rawScale === 4) return 4;
  if (rawScale === 2) return 2;
  return 3;
};

const normalizePolicySettings = (
  raw: Partial<TeacherPolicyResponse>,
  preferredScale?: AnyTemplateScale,
  fiveColorEnabled = false
): TeacherPolicyResponse => {
  const scale = normalizeScale(preferredScale ?? raw?.colorScale, fiveColorEnabled);
  return {
    ...raw,
    colorScale: scale,
    red: raw?.red || THREE_COLOR_DEFAULTS.red,
    yellow: raw?.yellow || THREE_COLOR_DEFAULTS.yellow,
    green: raw?.green || THREE_COLOR_DEFAULTS.green,
    orange: scale >= 4 ? (raw?.orange || FIVE_COLOR_DEFAULTS.orange) : undefined,
    blue: scale === 5 ? (raw?.blue || FIVE_COLOR_DEFAULTS.blue) : undefined
  };
};

const deriveTemplates = (raw: Partial<TeacherPolicyResponse> & {
  twoColorTemplate?: Partial<TeacherPolicyResponse>;
  threeColorTemplate?: Partial<TeacherPolicyResponse>;
  fourColorTemplate?: Partial<TeacherPolicyResponse>;
  fiveColorTemplate?: Partial<TeacherPolicyResponse>;
}, fiveColorEnabled: boolean) => {
  const twoColorTemplate = normalizePolicySettings(raw?.twoColorTemplate || raw, 2, fiveColorEnabled);
  const threeColorTemplate = normalizePolicySettings(raw?.threeColorTemplate || raw, 3, fiveColorEnabled);
  const fourColorTemplate = normalizePolicySettings(raw?.fourColorTemplate || raw, 4, fiveColorEnabled);
  const fiveColorTemplate = normalizePolicySettings(raw?.fiveColorTemplate || raw, 5, fiveColorEnabled);
  return { twoColorTemplate, threeColorTemplate, fourColorTemplate, fiveColorTemplate };
};

const TeacherPolicySettingsPage = () => {
  const navigate = useNavigate();
  const usePersistentSidebar = isStagingOrLocalhost();
  const [settings, setSettings] = useState<TeacherPolicyResponse>(DEFAULT_POLICY);
  const [lockStatus, setLockStatus] = useState<LockStatus>({
    lockedByAdmin: false,
    hasDomainAdmin: false,
    message: null
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [originalSettings, setOriginalSettings] = useState<TeacherPolicyResponse | null>(null);
  const [fiveColorScaleEnabled, setFiveColorScaleEnabled] = useState(false);
  const [scaleTemplates, setScaleTemplates] = useState<{
    twoColorTemplate: TeacherPolicyResponse;
    threeColorTemplate: TeacherPolicyResponse;
    fourColorTemplate: TeacherPolicyResponse;
    fiveColorTemplate: TeacherPolicyResponse;
  } | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfSubtitle, setPdfSubtitle] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const effectiveUser = getEffectiveUser();
  const impersonationMeta = getImpersonationMeta();
  const adminViewingTeacherPolicy = isImpersonating()
    && impersonationMeta?.actorRole === 'admin'
    && Boolean(effectiveUser)
    && !effectiveUser?.isAdmin;
  const showTeacherUtility = !adminViewingTeacherPolicy;

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }

    clearAllAuthStorage();

    const chromeApi = (window as any).chrome;
    if (chromeApi?.runtime?.sendMessage) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) {
          chromeApi.runtime.sendMessage(extensionId, message, () => {});
        } else {
          chromeApi.runtime.sendMessage(message, () => {});
        }
      } catch (error) {
        console.error('Sign-out storage clear failed:', error);
      }
    }

    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  useEffect(() => {
    document.title = 'Policy Settings - Traffic Lite Teacher';
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL_WITH_API}/teacher/policy-settings`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to fetch settings (${response.status})`);
      }

      const data: TeacherPolicyResponse & {
        activeTemplateScale?: AnyTemplateScale;
        twoColorTemplate?: TeacherPolicyResponse;
        threeColorTemplate?: TeacherPolicyResponse;
        fourColorTemplate?: TeacherPolicyResponse;
        fiveColorTemplate?: TeacherPolicyResponse;
      } = await response.json();
      const fiveColorEnabled = Boolean(data?.fiveColorScaleEnabled);
      setFiveColorScaleEnabled(fiveColorEnabled);
      const templates = deriveTemplates(data, fiveColorEnabled);
      setScaleTemplates(templates);
      const activeScale = normalizeScale(data?.activeTemplateScale ?? data?.colorScale, fiveColorEnabled);
      const effectiveActiveScale = activeScale === 5 && !fiveColorEnabled ? 4 : activeScale;
      const activeSettings =
        effectiveActiveScale === 5
          ? templates.fiveColorTemplate
          : (effectiveActiveScale === 4 ? templates.fourColorTemplate : (effectiveActiveScale === 2 ? templates.twoColorTemplate : templates.threeColorTemplate));
      setSettings(activeSettings);
      setOriginalSettings(activeSettings);
      if (data.lockStatus) {
        setLockStatus(data.lockStatus);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load policy settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (lockStatus.lockedByAdmin) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const token = getEffectiveToken();
      if (!token) {
        setError('Not authenticated. Please sign in again.');
        setSaving(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL_WITH_API}/teacher/policy-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          colorScale: settings.colorScale === 5 ? 5 : (settings.colorScale === 4 ? 4 : (settings.colorScale === 2 ? 2 : 3)),
          red: settings.red,
          ...(settings.colorScale >= 4 && settings.orange ? { orange: settings.orange } : {}),
          ...(settings.colorScale >= 3 && settings.yellow ? { yellow: settings.yellow } : {}),
          green: settings.green,
          ...(settings.colorScale === 5 && settings.blue ? { blue: settings.blue } : {})
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Failed to save (${response.status})`);
      }

      const templates = deriveTemplates({
        ...data,
        twoColorTemplate: data?.twoColorTemplate || scaleTemplates?.twoColorTemplate,
        threeColorTemplate: data?.threeColorTemplate || scaleTemplates?.threeColorTemplate,
        fourColorTemplate: data?.fourColorTemplate || scaleTemplates?.fourColorTemplate,
        fiveColorTemplate: data?.fiveColorTemplate || scaleTemplates?.fiveColorTemplate
      }, fiveColorScaleEnabled);
      setScaleTemplates(templates);
      const activeScale = normalizeScale(data?.activeTemplateScale ?? data?.colorScale, fiveColorScaleEnabled);
      const activeSettings =
        activeScale === 5
          ? templates.fiveColorTemplate
          : (activeScale === 4 ? templates.fourColorTemplate : (activeScale === 2 ? templates.twoColorTemplate : templates.threeColorTemplate));
      setSettings(activeSettings);
      setOriginalSettings(activeSettings);
      setSuccessMessage('Policy settings saved successfully!');
      if (data.lockStatus) {
        setLockStatus(data.lockStatus);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save policy settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (
    color: 'red' | 'orange' | 'yellow' | 'green' | 'blue',
    field: 'title' | 'description',
    value: string
  ) => {
    setSettings((prev) => ({
      ...prev,
      [color]: {
        ...(prev[color] || { title: '', description: '' }),
        [field]: value
      }
    }));
    setScaleTemplates((prev) => {
      if (!prev) return prev;
      const activeScale = settings.colorScale === 5 ? 5 : (settings.colorScale === 4 ? 4 : 3);
      const key = activeScale === 5 ? 'fiveColorTemplate' : (activeScale === 4 ? 'fourColorTemplate' : 'threeColorTemplate');
      const currentTemplate = prev[key];
      return {
        ...prev,
        [key]: {
          ...currentTemplate,
          [color]: {
            ...(currentTemplate[color] || { title: '', description: '' }),
            [field]: value
          }
        }
      };
    });
  };

  const getComparableSettings = (value: TeacherPolicyResponse | null) => {
    if (!value) return null;
    return {
      colorScale: value.colorScale === 5 ? 5 : (value.colorScale === 4 ? 4 : (value.colorScale === 2 ? 2 : 3)),
      red: value.red,
      orange: (value.colorScale || 3) >= 4 ? value.orange : undefined,
      yellow: (value.colorScale || 3) >= 3 ? value.yellow : undefined,
      green: value.green,
      blue: value.colorScale === 5 ? value.blue : undefined
    };
  };

  const hasUnsavedChanges =
    JSON.stringify(getComparableSettings(settings)) !== JSON.stringify(getComparableSettings(originalSettings));

  const hasEditPermission = !lockStatus.lockedByAdmin && !adminViewingTeacherPolicy;
  const inputsDisabled = !hasEditPermission || saving || loading;
  const saveDisabled = inputsDisabled || !hasUnsavedChanges;

  const handleColorScaleChange = (scale: 2 | 3 | 4 | 5) => {
    if (inputsDisabled) return;
    if (scale === 5 && !fiveColorScaleEnabled) return;
    if (scaleTemplates) {
      const nextTemplate =
        scale === 5
          ? scaleTemplates.fiveColorTemplate
          : (scale === 4 ? scaleTemplates.fourColorTemplate : (scale === 2 ? scaleTemplates.twoColorTemplate : scaleTemplates.threeColorTemplate));
      setSettings(normalizePolicySettings(nextTemplate, scale, fiveColorScaleEnabled));
      return;
    }
    setSettings((prev) => {
      if (scale === 2) {
        return normalizePolicySettings(
          {
            ...prev,
            colorScale: 2,
            red: THREE_COLOR_DEFAULTS.red,
            green: THREE_COLOR_DEFAULTS.green,
            orange: undefined,
            yellow: undefined,
            blue: undefined
          },
          2,
          fiveColorScaleEnabled
        );
      }
      if (scale === 4) {
        return normalizePolicySettings(
          {
            ...prev,
            colorScale: 4,
            red: FIVE_COLOR_DEFAULTS.red,
            orange: FIVE_COLOR_DEFAULTS.orange,
            yellow: FIVE_COLOR_DEFAULTS.yellow,
            green: FIVE_COLOR_DEFAULTS.green
          },
          4,
          fiveColorScaleEnabled
        );
      }
      if (scale === 5) {
        return normalizePolicySettings(
          {
            ...prev,
            colorScale: 5,
            red: FIVE_COLOR_DEFAULTS.red,
            orange: FIVE_COLOR_DEFAULTS.orange,
            yellow: FIVE_COLOR_DEFAULTS.yellow,
            green: FIVE_COLOR_DEFAULTS.green,
            blue: FIVE_COLOR_DEFAULTS.blue
          },
          5,
          fiveColorScaleEnabled
        );
      }
      return normalizePolicySettings(
        {
          ...prev,
          colorScale: 3,
          orange: undefined,
          blue: undefined
        },
        3,
        fiveColorScaleEnabled
      );
    });
  };

  const getActivePolicyColorsForPdf = (): PolicyPdfColorBlock[] => {
    const keys = settings.colorScale === 5
      ? (['red', 'orange', 'yellow', 'green', 'blue'] as const)
      : (settings.colorScale === 4
        ? (['red', 'orange', 'yellow', 'green'] as const)
        : (settings.colorScale === 2 ? (['red', 'green'] as const) : (['red', 'yellow', 'green'] as const)));
    return keys.map((key) => ({
      key,
      label: POLICY_META[key].label,
      accent: POLICY_META[key].accent,
      tint: POLICY_META[key].tint,
      title: settings[key]?.title || '',
      description: settings[key]?.description || ''
    }));
  };

  const handleExportPolicyPdf = async () => {
    setExportingPdf(true);
    try {
      await exportPolicyToPdf({
        title: pdfTitle,
        subtitle: pdfSubtitle,
        colorBlocks: getActivePolicyColorsForPdf(),
        fileNamePrefix: 'teacher-policy'
      });
      setExportModalOpen(false);
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div
        className={usePersistentSidebar ? 'max-w-[980px] mx-auto' : 'max-w-[980px] mx-auto'}
      >
        <div
          className="bg-white rounded-2xl shadow-md p-4 sm:p-6 lg:p-8"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
        <ImpersonationBanner />
        <div className="mb-8 relative">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 -mt-1">
                <img src="/icon.png" alt="Traffic Lite" className="w-10 h-10 object-contain" />
              </div>

              <div>
                <h1 className="text-[30px] font-bold" style={{ color: '#212529', marginBottom: '4px' }}>
                  Traffic Lite Teacher
                </h1>
                <p className="text-xl font-bold" style={{ color: '#374151' }}>
                  Policy Details
                </p>
                <p className="text-base" style={{ color: '#6b7280' }}>
                  Your personal policy settings for Traffic Lite.
                </p>
              </div>
            </div>
            {showTeacherUtility && (
              <HeaderUtilityMenu
                onSignOut={handleSignOut}
                includeExportPolicy
                onExportPolicy={() => setExportModalOpen(true)}
              />
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '16px', flexWrap: 'wrap' }}>
          {usePersistentSidebar ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#f9fafb'
              }}
            >
              <span style={{ fontWeight: 700, color: '#111827', fontSize: '17px' }}>Scale:</span>
              <select
                value={String(settings.colorScale || 3)}
                onChange={(e) => handleColorScaleChange(e.target.value === '5' ? 5 : (e.target.value === '4' ? 4 : (e.target.value === '2' ? 2 : 3)))}
                disabled={inputsDisabled}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827',
                  backgroundColor: 'white',
                  opacity: inputsDisabled ? 0.7 : 1
                }}
              >
                <option value="2">2 colors</option>
                <option value="3">3 colors</option>
                <option value="4">4 colors</option>
                {fiveColorScaleEnabled && <option value="5">5 colors</option>}
              </select>
            </div>
          ) : (
            <div />
          )}

          <span className="text-base font-medium text-gray-600">
            To use your AI-Usage Scale, go to{' '}
            <a
              href="https://classroom.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
              style={{ color: '#2563eb', fontWeight: 600 }}
            >
              Google Classroom
            </a>{' '}
            and create a post.
          </span>
          <button
            onClick={handleSave}
            disabled={saveDisabled}
            style={{
              padding: '10px 18px',
              backgroundColor: saveDisabled ? '#9ca3af' : '#4285f4',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: saveDisabled ? 'not-allowed' : 'pointer',
              opacity: saveDisabled ? 0.82 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {lockStatus.lockedByAdmin && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: '4px',
              color: '#9a3412',
              marginBottom: '20px'
            }}
          >
            {lockStatus.message || 'Policy settings locked by admin.'}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c00',
              marginBottom: '20px'
            }}
          >
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#efe',
              border: '1px solid #8f8',
              borderRadius: '4px',
              color: '#080',
              marginBottom: '20px'
            }}
          >
            {successMessage}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            Loading settings...
          </div>
        )}

        {!loading && (
          <div style={{ marginBottom: '30px' }}>
            <div className="space-y-5">
              {(settings.colorScale === 5
                ? (['red', 'orange', 'yellow', 'green', 'blue'] as const)
                : (settings.colorScale === 4
                  ? (['red', 'orange', 'yellow', 'green'] as const)
                  : (settings.colorScale === 2
                    ? (['red', 'green'] as const)
                    : (['red', 'yellow', 'green'] as const)))
              ).map((color) => (
                <div
                  key={color}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-lg focus-within:shadow-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 focus-within:border-blue-200"
                  style={{
                    borderLeft: `12px solid ${POLICY_META[color].accent}`,
                    backgroundColor: POLICY_META[color].tint,
                    boxShadow: inputsDisabled ? '0 4px 10px rgba(15,23,42,0.06)' : undefined,
                    cursor: hasEditPermission ? 'pointer' : 'default'
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '9999px',
                          backgroundColor: POLICY_META[color].accent
                        }}
                      />
                      <span className="text-sm font-semibold text-gray-600">{POLICY_META[color].label}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-6">
                      <div className="md:col-span-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Title</div>
                        {!hasEditPermission ? (
                          <div
                            style={{
                              fontSize: '16px',
                              fontWeight: 600,
                              lineHeight: 1.4,
                              color: '#111827',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {settings[color]?.title || ''}
                          </div>
                        ) : (
                          <textarea
                            value={settings[color]?.title || ''}
                            onChange={(e) => handleFieldChange(color, 'title', e.target.value)}
                            rows={3}
                            disabled={inputsDisabled}
                            className="policy-editable-field w-full rounded-xl px-3 py-2 text-[15px] font-semibold leading-[1.35] text-gray-900 outline-none resize-none disabled:opacity-70"
                          />
                        )}
                      </div>
                      <div className="md:col-span-8">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Description</div>
                        {!hasEditPermission ? (
                          <div
                            style={{
                              fontSize: '15px',
                              lineHeight: 1.65,
                              color: '#4b5563',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {settings[color]?.description || ''}
                          </div>
                        ) : (
                          <textarea
                            value={settings[color]?.description || ''}
                            onChange={(e) => handleFieldChange(color, 'description', e.target.value)}
                            rows={3}
                            disabled={inputsDisabled}
                            className="policy-editable-field w-full rounded-xl px-3 py-2 text-[14px] leading-[1.55] text-gray-600 outline-none resize-y disabled:opacity-70"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
        {showTeacherUtility && <FeedbackFab />}
        {exportModalOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15,23,42,0.42)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px'
            }}
          >
            <div style={{ width: '100%', maxWidth: '460px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #d1d5db', padding: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#111827' }}>Export Policy to PDF</h3>
              <p style={{ marginTop: '8px', marginBottom: '12px', fontSize: '13px', color: '#4b5563' }}>
                Optional title and subtitle will be shown above your policy content in the exported PDF.
              </p>
              <div style={{ display: 'grid', gap: '10px' }}>
                <input
                  value={pdfTitle}
                  onChange={(e) => setPdfTitle(e.target.value)}
                  placeholder="Title (optional)"
                  style={{ padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                />
                <input
                  value={pdfSubtitle}
                  onChange={(e) => setPdfSubtitle(e.target.value)}
                  placeholder="Subtitle (optional)"
                  style={{ padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setExportModalOpen(false)}
                  disabled={exportingPdf}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleExportPolicyPdf()}
                  disabled={exportingPdf}
                  style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', backgroundColor: exportingPdf ? '#9ca3af' : '#4285f4', color: 'white', fontWeight: 700, cursor: exportingPdf ? 'not-allowed' : 'pointer' }}
                >
                  {exportingPdf ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default TeacherPolicySettingsPage;
